import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import PageMeta from '../../components/common/PageMeta';
import PageBreadcrumb from '../../components/common/PageBreadCrumb';
import Button from '../../components/ui/button/Button';
import { Modal } from '../../components/ui/modal';
import { useModal } from '../../hooks/useModal';
import { useToast } from '../../contexts/ToastContext';
import { listSessions, abandonSession, W3WizardListItem, W3WizardStatus } from '../../lib/api/w3-wizard';
import JurisdictionBadge, { jurisdictionFromApiNumber } from '../../components/ui/JurisdictionBadge';

const STATUS_CONFIG: Record<
  W3WizardStatus,
  { label: string; className: string; pulse?: boolean }
> = {
  created: { label: "Created", className: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400" },
  uploading: { label: "Uploading", className: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400" },
  parsing: { label: "Parsing", className: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400", pulse: true },
  parsed: { label: "Parsed", className: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" },
  reconciled: { label: "Reconciled", className: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" },
  justifying: { label: "Justifying", className: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" },
  ready: { label: "Ready", className: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" },
  generating: { label: "Generating", className: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400", pulse: true },
  completed: { label: "Completed", className: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" },
  abandoned: { label: "Abandoned", className: "bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-400" },
  importing_plan: { label: "Importing Plan", className: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400", pulse: true },
  plan_imported: { label: "Plan Imported", className: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" },
  plan_verified: { label: "Plan Verified", className: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" },
};

function StatusBadge({ status }: { status: W3WizardStatus }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.created;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.className}`}>
      {cfg.pulse && (
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500" />
        </span>
      )}
      {cfg.label}
    </span>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

const STATUS_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "All Statuses" },
  { value: "created", label: "Created" },
  { value: "uploading", label: "Uploading" },
  { value: "parsing", label: "Parsing" },
  { value: "parsed", label: "Parsed" },
  { value: "reconciled", label: "Reconciled" },
  { value: "justifying", label: "Justifying" },
  { value: "ready", label: "Ready" },
  { value: "generating", label: "Generating" },
  { value: "completed", label: "Completed" },
  { value: "abandoned", label: "Abandoned" },
];

export default function W3WizardList() {
  const navigate = useNavigate();
  const { error: showError, success: showSuccess } = useToast();

  const [sessions, setSessions] = useState<W3WizardListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [abandoningSession, setAbandoningSession] = useState<W3WizardListItem | null>(null);
  const [abandoning, setAbandoning] = useState(false);
  const abandonModal = useModal();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await listSessions(statusFilter ? { status: statusFilter } : undefined);
        setSessions(data);
      } catch (err) {
        showError("Load Failed", err instanceof Error ? err.message : "Failed to load wizard sessions.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [statusFilter]);

  const openAbandonModal = (session: W3WizardListItem) => {
    setAbandoningSession(session);
    abandonModal.openModal();
  };

  const handleAbandon = async () => {
    if (!abandoningSession) return;
    setAbandoning(true);
    try {
      await abandonSession(abandoningSession.id);
      setSessions((prev) =>
        prev.map((s) =>
          s.id === abandoningSession.id ? { ...s, status: "abandoned" as W3WizardStatus } : s
        )
      );
      showSuccess("Session Abandoned", "The report session has been abandoned.");
      abandonModal.closeModal();
    } catch (err) {
      showError("Abandon Failed", err instanceof Error ? err.message : "Failed to abandon session.");
    } finally {
      setAbandoning(false);
    }
  };

  return (
    <>
      <PageMeta
        title="Post Plugging Report | RegulAgent"
        description="Manage post plugging report sessions"
      />
      <PageBreadcrumb pageTitle="Post Plugging Report" />

      <div className="space-y-5">
        {/* Header bar */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-xl font-semibold text-gray-800 dark:text-white/90">
              Post Plugging Report Sessions
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Create and resume post plugging report sessions
            </p>
          </div>
          <Button onClick={() => navigate("/w3-wizard/new")}>
            New Session
          </Button>
        </div>

        {/* Filter bar */}
        <div className="flex items-center gap-3">
          <label className="text-sm text-gray-600 dark:text-gray-400 font-medium whitespace-nowrap">
            Filter by status:
          </label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-sm border border-gray-300 dark:border-white/[0.1] rounded-lg px-3 py-1.5 bg-white dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          >
            {STATUS_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Content */}
        <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-r-transparent" />
                <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">Loading sessions...</p>
              </div>
            </div>
          ) : sessions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              <svg className="w-12 h-12 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <div className="text-center">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  No wizard sessions yet.
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Create one to get started.
                </p>
              </div>
              <Button onClick={() => navigate("/w3-wizard/new")}>
                Create Session
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-white/[0.05] bg-gray-50 dark:bg-white/[0.02]">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">API Number</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Jurisdiction</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Step</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Created</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Last Updated</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                  {sessions.map((session) => (
                    <tr
                      key={session.id}
                      className="hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors"
                    >
                      <td className="px-4 py-3 font-medium text-gray-800 dark:text-white/90 whitespace-nowrap">
                        {session.api_number}
                      </td>
                      <td className="px-4 py-3">
                        <JurisdictionBadge jurisdiction={jurisdictionFromApiNumber(session.api_number)} />
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={session.status} />
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                        {session.current_step}
                      </td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {formatDate(session.created_at)}
                      </td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {formatDate(session.updated_at)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {session.status !== "abandoned" && (
                            <button
                              onClick={() => navigate(`/w3-wizard/${session.id}`)}
                              className="text-sm text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 font-medium transition-colors"
                            >
                              {session.status === "completed" ? "View" : "Resume"}
                            </button>
                          )}
                          {session.status !== "abandoned" && session.status !== "completed" && (
                            <button
                              onClick={() => openAbandonModal(session)}
                              className="text-sm text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-medium transition-colors"
                            >
                              Abandon
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Abandon Confirmation Modal */}
      <Modal
        isOpen={abandonModal.isOpen}
        onClose={abandonModal.closeModal}
        className="max-w-[500px] p-5 lg:p-10"
      >
        <div className="text-center">
          <div className="relative flex items-center justify-center z-1 mb-7">
            <svg
              className="fill-error-50 dark:fill-error-500/15"
              width="90"
              height="90"
              viewBox="0 0 90 90"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M34.364 6.85053C38.6205 -2.28351 51.3795 -2.28351 55.636 6.85053C58.0129 11.951 63.5594 14.6722 68.9556 13.3853C78.6192 11.0807 86.5743 21.2433 82.2185 30.3287C79.7862 35.402 81.1561 41.5165 85.5082 45.0122C93.3019 51.2725 90.4628 63.9451 80.7747 66.1403C75.3648 67.3661 71.5265 72.2695 71.5572 77.9156C71.6123 88.0265 60.1169 93.6664 52.3918 87.3184C48.0781 83.7737 41.9219 83.7737 37.6082 87.3184C29.8831 93.6664 18.3877 88.0266 18.4428 77.9156C18.4735 72.2695 14.6352 67.3661 9.22531 66.1403C-0.462787 63.9451 -3.30193 51.2725 4.49185 45.0122C8.84391 41.5165 10.2138 35.402 7.78151 30.3287C3.42572 21.2433 11.3808 11.0807 21.0444 13.3853C26.4406 14.6722 31.9871 11.951 34.364 6.85053Z"
                fill=""
                fillOpacity=""
              />
            </svg>
            <span className="absolute -translate-x-1/2 -translate-y-1/2 left-1/2 top-1/2">
              <svg
                className="fill-error-600 dark:fill-error-500"
                width="38"
                height="38"
                viewBox="0 0 38 38"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M9.62684 11.7496C9.04105 11.1638 9.04105 10.2141 9.62684 9.6283C10.2126 9.04252 11.1624 9.04252 11.7482 9.6283L18.9985 16.8786L26.2485 9.62851C26.8343 9.04273 27.7841 9.04273 28.3699 9.62851C28.9556 10.2143 28.9556 11.164 28.3699 11.7498L21.1198 18.9999L28.3699 26.25C28.9556 26.8358 28.9556 27.7855 28.3699 28.3713C27.7841 28.9571 26.8343 28.9571 26.2485 28.3713L18.9985 21.1212L11.7482 28.3715C11.1624 28.9573 10.2126 28.9573 9.62684 28.3715C9.04105 27.7857 9.04105 26.836 9.62684 26.2502L16.8771 18.9999L9.62684 11.7496Z"
                  fill=""
                />
              </svg>
            </span>
          </div>

          <h4 className="mb-2 text-2xl font-semibold text-gray-800 dark:text-white/90 sm:text-title-sm">
            Abandon Report?
          </h4>
          <p className="text-sm leading-6 text-gray-500 dark:text-gray-400">
            Are you sure you want to abandon the session for well{" "}
            <span className="font-medium text-gray-700 dark:text-gray-300">
              {abandoningSession?.api_number}
            </span>
            ? This action cannot be undone.
          </p>

          <div className="flex items-center justify-center w-full gap-3 mt-7">
            <Button
              size="sm"
              variant="outline"
              onClick={abandonModal.closeModal}
              disabled={abandoning}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleAbandon}
              disabled={abandoning}
              className="bg-error-500 hover:bg-error-600"
            >
              {abandoning ? "Abandoning..." : "Abandon"}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
